import { FC, useEffect, useState } from 'react';
import { CatLayout } from 'blocks-nextjs/blocks-react/components/Layout';
import { CatLayoutSection } from 'blocks-nextjs/blocks-react/components/LayoutSection';
import PartsDiagram from 'components/parts-diagram';
import PartsTable from '../../../parts-table/parts-table';
import { getPartsDetails } from 'services/parts-diagram/actions';
import useURLParams from 'hooks/useURLParams';
import useSvgContent from 'services/parts-diagram/get-svg-url';
import { CatSkeleton } from '@blocks-react/components/Skeleton';
import PartsOverview from 'components/parts-overview';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import {
  AnalyticsCommonInformation,
  CATEGORY_TYPES,
  LineItem,
  ListOfArrangementsResponse
} from 'types/parts-diagram';
import PartsDetailsTitle from './parts-details-title';

interface PartsDetailsProps extends AnalyticsCommonInformation {
  listOfArrangementsData: ListOfArrangementsResponse;
}

const PartsDetails: FC<PartsDetailsProps> = ({
  listOfArrangementsData,
  dealerData,
  locale
}) => {
  const {
    partsDetails,
    handlePartsContentCategory,
    handleSelectedPart,
    analyticsCommonInfo,
    setAnalyticsCommonInfo
  } = usePartDiagram();
  const { setParamsWithWindows, deleteParams, checkParamsHas } = useURLParams();

  const { groupPart: { partNumber = '', name = '' } = {}, graphics = [] } =
    partsDetails || {};
  const shouldDisplayTableView = graphics.length === 0;
  const { data: svgContent, isLoading } = useSvgContent(
    !shouldDisplayTableView && graphics[0].graphicBlobUrl
  );

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  useEffect(() => {
    setAnalyticsCommonInfo({
      ...analyticsCommonInfo,
      dealerData,
      locale,
      model: listOfArrangementsData.serialNumber,
      currency: partsDetails?.currency
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locale,
    dealerData,
    partsDetails?.currency,
    listOfArrangementsData.serialNumber
  ]);

  const handleHover = (refNumber: string | null) => {
    setHoveredItem(refNumber);
  };

  const handlePartView = async (lineItem: LineItem) => {
    handlePartsContentCategory(CATEGORY_TYPES.LOADER);
    const {
      ieSystemControlNumber = '',
      componentId = '',
      partNumber = ''
    } = lineItem || {};
    checkParamsHas('systemId') && deleteParams('systemId');
    setParamsWithWindows('componentId', componentId);
    setParamsWithWindows('ieSystemControlNumber', ieSystemControlNumber);

    if (!ieSystemControlNumber) {
      return;
    }
    const { partDetailsAndSchematicResponse } = await getPartsDetails(
      ieSystemControlNumber
    );
    if (partDetailsAndSchematicResponse) {
      handlePartsContentCategory(
        CATEGORY_TYPES.PARTSDETAILS,
        null,
        partDetailsAndSchematicResponse
      );
    }
    const pNumber = partNumber?.replace(/-/g, '');
    handleSelectedPart({ partNumber: pNumber, ieSystemControlNumber });
  };

  if (isLoading) {
    return <CatSkeleton size="xxl" />;
  }

  const renderCondition = () => {
    return !shouldDisplayTableView && !!svgContent ? (
      <CatLayout
        style={{
          ['--cat-sidebar-width' as string]: '405px'
        }}
        variant="right-sidebar"
        gap="sm"
        break="slower"
      >
        <CatLayoutSection>
          <div className="flex-grow-1">
            <PartsDiagram
              hoveredItem={hoveredItem}
              partDetailsAndSchematicsData={partsDetails}
              svgContent={svgContent}
              isLoading={isLoading}
            />
          </div>
        </CatLayoutSection>

        <CatLayoutSection>
          <PartsOverview
            partDetailsAndSchematicsData={partsDetails}
            handlePartView={handlePartView}
            onHover={handleHover}
          />
        </CatLayoutSection>
      </CatLayout>
    ) : (
      <PartsTable
        partDetailsAndSchematicsData={partsDetails}
        handlePartView={handlePartView}
        fromProductPage={false}
      />
    );
  };

  return (
    <>
      <PartsDetailsTitle partNumber={partNumber} partName={name} />
      {renderCondition()};
    </>
  );
};

export default PartsDetails;
